import { PublicClientApplication, AccountInfo, SilentRequest, AuthenticationResult, InteractionRequiredAuthError, AuthError } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";

// Create MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Azure best practice: Token cache for performance optimization
interface TokenCacheItem {
  token: string;
  expiresAt: number;
}

// Azure best practice: Cache tokens by scope to avoid unnecessary network requests
const tokenCache: Record<string, TokenCacheItem> = {};

// Track initialization state to prevent multiple initializations
let isInitialized = false;
let initializePromise: Promise<void> | null = null;

// Initialize MSAL and handle redirects properly
const initializeMsal = async (): Promise<void> => {
  if (initializePromise) {
    return initializePromise;
  }

  if (isInitialized) {
    return;
  }

  initializePromise = (async () => {
    try {
      // First, initialize MSAL
      await msalInstance.initialize();
      console.log('[AuthService] MSAL initialized successfully');
      
      // Then handle any redirect promise
      const response = await msalInstance.handleRedirectPromise();
      
      // Azure best practice: Update token cache when handling redirects
      if (response) {
        const scopes = response.scopes.join(' ');
        tokenCache[scopes] = {
          token: response.accessToken,
          expiresAt: new Date().getTime() + (response.expiresOn?.getTime() || 3600 * 1000)
        };
        console.log('[AuthService] Redirect handled successfully');
      }
      
      isInitialized = true;
    } catch (error) {
      console.error("[AuthService] Initialization or redirect handling error:", error);
      // Don't throw - let the app continue even if redirect handling fails
    }
  })();

  return initializePromise;
};

// Ensure MSAL is initialized before any operation
const ensureInitialized = async (): Promise<void> => {
  if (!isInitialized) {
    await initializeMsal();
  }
};

// Export initialization function for app startup
export const initializeAuth = initializeMsal;

// Function to get current authenticated user
export const getCurrentUser = async (): Promise<AccountInfo | null> => {
  await ensureInitialized();
  const accounts = msalInstance.getAllAccounts();
  if (!accounts || accounts.length === 0) {
    return null;
  }
  return accounts[0];
};

/**
 * Azure best practice: Get access token with efficient caching 
 * and proper error handling for SharePoint Embedded
 */
export const getAccessToken = async (scopes: string | string[]): Promise<string> => {
  await ensureInitialized();
  
  const scopesArray = Array.isArray(scopes) ? scopes : [scopes];
  const scopeKey = scopesArray.join(' ');
  
  // Azure best practice: Check cache before making network request
  const cachedItem = tokenCache[scopeKey];
  if (cachedItem && cachedItem.expiresAt > Date.now() + 60000) { // 1 minute buffer
    return cachedItem.token;
  }
  
  const account = await getCurrentUser();
  if (!account) {
    throw new Error("No active account! Sign in required.");
  }
  
  const request: SilentRequest = {
    scopes: scopesArray,
    account: account
  };
  
  // Debug: Log the exact scopes being requested
  console.info("[CopilotAuthProvider] Requesting scopes:", scopesArray);
  
  try {
    // Try silent token acquisition first (recommended pattern)
    const response: AuthenticationResult = await msalInstance.acquireTokenSilent(request);
    
    // Azure best practice: Update cache with new token
    tokenCache[scopeKey] = {
      token: response.accessToken,
      expiresAt: new Date().getTime() + (response.expiresOn?.getTime() || 3600 * 1000)
    };
    
    return response.accessToken;
  } catch (error) {
    // Azure best practice: Clear cache for this scope on error
    delete tokenCache[scopeKey];
    
    // Azure best practice: Handle interaction required errors specifically 
    if (error instanceof InteractionRequiredAuthError) {
      try {
        // Debug: Log the exact scopes being requested for popup
        console.info("[CopilotAuthProvider] Requesting scopes via popup:", scopesArray);
        
        // Azure best practice: Use popup for better UX in SPA
        const response = await msalInstance.acquireTokenPopup({
          ...request,
          redirectUri: window.location.origin
        });
        
        // Update cache with new token
        tokenCache[scopeKey] = {
          token: response.accessToken,
          expiresAt: new Date().getTime() + (response.expiresOn?.getTime() || 3600 * 1000)
        };
        
        return response.accessToken;
      } catch (popupError) {
        // Azure best practice: Detailed error handling and logging
        let errorMessage = "Failed to acquire token interactively";
        if (popupError instanceof AuthError) {
          errorMessage += `: ${popupError.errorCode} - ${popupError.errorMessage}`;
        } else if (popupError instanceof Error) {
          errorMessage += `: ${popupError.message}`;
        }
        
        console.error(errorMessage, popupError);
        throw new Error(errorMessage);
      }
    }
    
    // Azure best practice: Enhanced error messages for better debugging
    let errorMessage = "Token acquisition failed";
    if (error instanceof AuthError) {
      errorMessage += `: ${error.errorCode} - ${error.errorMessage}`;
    } else if (error instanceof Error) {
      errorMessage += `: ${error.message}`;
    }
    
    console.error(errorMessage, error);
    throw new Error(errorMessage);
  }
};

/**
 * Azure best practice: Clear token cache when needed
 * (e.g., when signing out or when tokens become invalid)
 */
export const clearTokenCache = (): void => {
  Object.keys(tokenCache).forEach(key => {
    delete tokenCache[key];
  });
};

/**
 * Function to check if user has specific roles
 */
export const hasRole = async (requiredRoles: string | string[]): Promise<boolean> => {
  const currentAccount = await getCurrentUser();
  
  if (!currentAccount) {
    return false;
  }
  
  // Check for roles in the ID token claims
  const idTokenClaims = currentAccount.idTokenClaims as { roles?: string[] };
  const userRoles = idTokenClaims?.roles || [];
  
  if (Array.isArray(requiredRoles)) {
    return requiredRoles.some(role => userRoles.includes(role));
  }
  
  return userRoles.includes(requiredRoles);
};
